import { useState, useMemo, useCallback } from "react";
import { documentService } from "../services/documents.service";
import { mediaService } from "../services/media.service";
import actionableInsightsService from "../services/actionable-insights.service";
import type {
  CreateDocumentDto,
  Document,
  DocumentDTO,
  DocumentsResponse,
} from "../types/document.dto";
import { DocumentSource } from "../types/document.dto";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useRef } from "react";
import { extractDocumentYears } from "../utils/document-utils";

export function buildDocumentChipFilters(chipFilters: {
  isImportant: boolean;
  isPrivate: boolean;
  availableOffline: boolean;
  category?: number | null;
}) {
  const filterEntries = [];
  if (chipFilters.isImportant)
    filterEntries.push({
      isImportant: { operation: "EQUAL", value: true, type: "BOOLEAN" },
    });
  if (chipFilters.isPrivate)
    filterEntries.push({
      isPrivate: { operation: "EQUAL", value: true, type: "BOOLEAN" },
    });
  if (chipFilters.availableOffline)
    filterEntries.push({
      availableOffline: { operation: "EQUAL", value: true, type: "BOOLEAN" },
    });
  if (Array.isArray(chipFilters.category) && chipFilters.category.length > 0) {
    filterEntries.push({
      category: {
        operation: "in",
        value: chipFilters.category,
        type: "number",
      },
    });
  } else if (typeof chipFilters.category === "number") {
    filterEntries.push({
      category: {
        operation: "equal",
        value: chipFilters.category,
        type: "number",
      },
    });
  }
  let filters: any = {};
  if (filterEntries.length === 1) {
    filters = filterEntries[0];
  } else if (filterEntries.length > 1) {
    filters = { $or: filterEntries };
  }
  return filters;
}

export function getAvailableYears(
  documents: { documentDate?: string }[]
): number[] {
  return extractDocumentYears(documents);
}

// Utility to build the year filter payload for documents
export function buildYearFilter(selectedYears: number[]): any {
  if (!selectedYears || selectedYears.length === 0) return undefined;
  if (selectedYears.length === 1) {
    const year = selectedYears[0];
    return {
      operation: "between",
      value: [`${year}-01-01T00:00:00.000Z`, `${year + 1}-01-01T00:00:00.000Z`],
      type: "DATE",
    };
  } else if (selectedYears.length > 1) {
    return {
      operation: "or_between",
      value: selectedYears.map((year) => [
        `${year}-01-01T00:00:00.000Z`,
        `${year + 1}-01-01T00:00:00.000Z`,
      ]),
      type: "DATE",
    };
  }
  return undefined;
}

export interface DocumentTypeCounts {
  prescriptions: number;
  labReports: number;
  dischargeSummaries: number;
  medicalReceipts: number;
  insurances: number;
  uncategorized?: number;
  other?: number;
}

export function useDocument(memberId: string) {
  // Fetch function that receives a FilterDto (filters, sort, pagination)
  const fetchDocuments = async (dto: any): Promise<DocumentsResponse> => {
    // Clone the DTO to avoid mutating the original object (immutability best practice)
    const dtoClone = JSON.parse(JSON.stringify(dto));
    // Ensure member_id is set as a top-level field in the payload
    if (!dtoClone.member_id && memberId) {
      dtoClone.member_id = memberId;
    }
    // Optionally, still inject member_id into filters for legacy backend compatibility
    if (dtoClone.filters && !dtoClone.filters.member_id && memberId) {
      dtoClone.filters.memberId = {
        operation: "EQUAL",
        value: memberId,
        type: "STRING",
      };
    }
    // Patch sort for documents API: { key, direction } -> { field, order }
    if (dtoClone.sort && Array.isArray(dtoClone.sort)) {
      dtoClone.sort = dtoClone.sort.map((s: any) => ({
        field: s.key,
        order: s.direction,
      }));
    }
    // Call the service with the correct DTO
    const service = documentService(String(memberId));
    const response = (await service.getAllDocuments(dtoClone)) as any;
    return {
      data: response?.data || [],
      total: response?.total || 0,
    };
  };

  // Fetch documents with settings merged
  const fetchDocumentsWithSettings = async (
    dto: any
  ): Promise<DocumentsResponse> => {
    const dtoClone = JSON.parse(JSON.stringify(dto));
    if (!dtoClone.member_id && memberId) {
      dtoClone.member_id = memberId;
    }
    if (dtoClone.filters && !dtoClone.filters.member_id && memberId) {
      dtoClone.filters.memberId = {
        operation: "EQUAL",
        value: memberId,
        type: "STRING",
      };
    }
    if (dtoClone.sort && Array.isArray(dtoClone.sort)) {
      dtoClone.sort = dtoClone.sort.map((s: any) => ({
        field: s.key,
        order: s.direction,
      }));
    }
    const service = documentService(String(memberId));
    const response = (await service.getAllDocumentsWithSettings(
      dtoClone
    )) as any;
    return {
      data: response?.data || [],
      total: response?.total || 0,
    };
  };

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const documentsService = useMemo(() => documentService(memberId), [memberId]);

  const { showToast } = useToast();

  const downloadDocument = async (fileKey: string, fileName?: string) => {
    if (!fileKey) return;
    setDownloading(true);
    try {
      const blob = await mediaService.download(String(memberId), fileKey);
      const url = window.URL.createObjectURL(blob);
      const doc = typeof window !== "undefined" ? window.document : null;
      if (doc) {
        const a = doc.createElement("a");
        a.href = url;
        a.download = fileName || "download";
        doc.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        window.open(url, "_blank");
      }
      // showToast({ position: 'top', message: 'Download started', variant: 'success', duration: 4000 });
    } catch (e: any) {
      showToast({
        position: "top",
        message: "Please try again",
        variant: "error",
        duration: 5000,
      });
    } finally {
      setDownloading(false);
    }
  };

  const createDocument = async (doc: CreateDocumentDto) => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      if (!doc.source) {
        doc.source = DocumentSource.APP;
      }
      const result = await documentsService.createDocument(doc);
      setSaveSuccess(true);
      return result;
    } catch (e: any) {
      setSaveError(e.message || "Failed to add document");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const uploadDocument = async (
    file: File,
    source: string = DocumentSource.APP
  ): Promise<any> => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const result = await documentsService.uploadDocument(file, source);
      setSaveSuccess(true);
      return result;
    } catch (e: any) {
      setSaveError(e.message || "Failed to upload document");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const getAllDocuments = async (params: Record<string, any> = {}) => {
    return await documentsService.getAllDocuments(params);
  };

  const getDocumentTypeCounts = async (options?: {
    mode?: "healthMemories" | "docsOnly" | "landing";
  }) => {
    return await documentsService.getDocumentTypeCounts(options);
  };

  // Debounced share document logic
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const shareDocumentWithDebounce = (
    email: string,
    documentId: string,
    setLoading: (v: boolean) => void,
    setEmail: (v: string) => void,
    onOpenChange: (v: boolean) => void
  ) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      if (!email || !documentId) return;
      setLoading(true);
      try {
        const normalizedMemberId = String(memberId);
        const docService = documentService(normalizedMemberId);
        const payload = { documentId, email };
        await docService.shareDocument(payload);
        // showToast({ position: 'top',
        //   message: `An email has been sent to ${email} with a share link.`,
        //   variant: 'success',
        //   duration: 4000
        // });
        setEmail("");
        onOpenChange(false);
      } catch (err: any) {
        // showToast({ position: 'top',
        //   message: `Failed to share document: ${err?.message || 'Could not send share email.'}`,
        //   variant: 'error',
        //   duration: 5000
        // });
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  /**
   * Update a boolean flag (offline_access, important, private) for a document.
   * isPrivate updates the document directly, others use document_settings collection.
   * @param doc The document object (must have _id or id)
   * @param flag The flag name ("availableOffline", "isPrivate", or "isImportant")
   * @param value The boolean value to set
   * @param updateLocal Optional callback to update local state (e.g., setDocuments)
   */
  const updateDocumentFlag = async (
    doc: any,
    flag: "availableOffline" | "isPrivate" | "isImportant",
    value: boolean,
    updateLocal?: (updater: (prev: any[]) => any[]) => void
  ) => {
    if (!doc || (!doc._id && !doc.id)) return;
    try {
      if (flag === "isPrivate") {
        // isPrivate updates the document directly via documents service
        await documentsService.updateDocument(doc._id || doc.id, {
          [flag]: value,
        });
      } else {
        // availableOffline and isImportant use document_settings
        const documentSettingsService = (
          await import("@/services/document-settings.service")
        ).default;
        await documentSettingsService.updateFlag(
          memberId,
          doc._id || doc.id,
          flag,
          value
        );
      }

      if (updateLocal) {
        updateLocal((prev: any[]) =>
          prev.map((d) =>
            (d._id || d.id) === (doc._id || doc.id)
              ? { ...d, [flag]: value }
              : d
          )
        );
      }
    } catch (e: any) {
      // showToast({ position: 'top',
      //   message: `Failed to update ${flag.replace('_', ' ')}: ${e?.message || ''}`,
      //   variant: 'error',
      //   duration: 5000
      // });
    }
  };

  /**
   * Delete a document by id (or doc object with _id/id).
   * @param doc Document object or id
   * @param updateLocal Optional callback to update local state (e.g., setDocuments)
   */
  const deleteDocument = async (
    doc: any,
    updateLocal?: (updater: (prev: any[]) => any[]) => void
  ) => {
    const docId = typeof doc === "string" ? doc : doc?._id || doc?.id;
    if (!docId) return;
    try {
      await documentsService.deleteDocument(docId);
      if (updateLocal) {
        updateLocal((prev: any[]) =>
          prev.filter((d) => (d._id || d.id) !== docId)
        );
      }
      // showToast({ position: 'top', message: 'Document deleted successfully', variant: 'success', duration: 4000 });
    } catch (e: any) {
      showToast({
        position: "top",
        message: `Please try again`,
        variant: "error",
        duration: 5000,
      });
      throw e;
    }
  };

  /**
   * Update the notes field for a document.
   * @param doc Document object (must have _id or id)
   * @param notes New notes string
   * @param updateLocal Optional callback to update local state (e.g., setDocuments)
   */
  const updateDocumentNotes = async (
    doc: any,
    notes: string,
    updateLocal?: (updater: (prev: any[]) => any[]) => void
  ) => {
    if (!doc || (!doc._id && !doc.id)) return;
    try {
      await documentsService.updateDocument(doc._id || doc.id, {
        notes,
        updateComment: true,
      });
      if (updateLocal) {
        updateLocal((prev: any[]) =>
          prev.map((d) =>
            (d._id || d.id) === (doc._id || doc.id) ? { ...d, notes } : d
          )
        );
      }
      // showToast({ position: 'top', message: 'Comment updated', variant: 'success', duration: 4000 });
    } catch (e: any) {
      showToast({
        position: "top",
        message: `Please try again`,
        variant: "error",
        duration: 5000,
      });
      throw e;
    }
  };

  /**
   * Bulk update a boolean flag for multiple documents.
   * @param docs Array of document objects (must have _id or id)
   * @param flag The flag name ("availableOffline", "isPrivate", or "isImportant")
   * @param value The boolean value to set
   * @param updateLocal Optional callback to update local state (e.g., setDocuments)
   */
  const bulkUpdateFlag = async (
    docs: any[],
    flag: "availableOffline" | "isPrivate" | "isImportant",
    value: boolean,
    updateLocal?: (updater: (prev: any[]) => any[]) => void
  ) => {
    if (!docs || docs.length === 0) return;
    try {
      const ids = docs.map((doc) => doc._id || doc.id);
      const res = (await documentsService.bulkUpdateDocuments(ids, {
        [flag]: value,
      })) as any;
      if (updateLocal && res?.updated) {
        const updatedIds = new Set(
          (res.updated as any[]).map((d: any) => d._id || d.id)
        );
        updateLocal((prev: any[]) =>
          prev.map((d) =>
            updatedIds.has(d._id || d.id) ? { ...d, [flag]: value } : d
          )
        );
      }
      const flagLabel =
        flag === "availableOffline"
          ? "offline"
          : flag.replace(/^is/, "").toLowerCase();
    } catch (e: any) {
      showToast({
        position: "top",
        message: `Please try again`,
        variant: "error",
        duration: 5000,
      });
    }
  };

  /**
   * Bulk delete documents.
   * @param docs Array of document objects or ids
   * @param updateLocal Optional callback to update local state
   */
  const bulkDelete = async (
    docs: any[],
    updateLocal?: (updater: (prev: any[]) => any[]) => void
  ) => {
    if (!docs || docs.length === 0) return;
    try {
      const ids = docs.map((doc) =>
        typeof doc === "string" ? doc : doc._id || doc.id
      );
      const res = (await documentsService.bulkDeleteDocuments(ids)) as any;
      if (updateLocal && res?.deleted) {
        const deletedIds = new Set(
          (res.deleted as any[]).map((d: any) => d._id || d.id)
        );
        updateLocal((prev: any[]) =>
          prev.filter((d) => !deletedIds.has(d._id || d.id))
        );
      }
      // showToast({ position: 'top', message: `Deleted ${docs.length} documents`, variant: 'success', duration: 4000 });
    } catch (e: any) {
      showToast({
        position: "top",
        message: `Please try again`,
        variant: "error",
        duration: 5000,
      });
    }
  };

  /**
   * Bulk download documents as zip.
   * @param docs Array of document objects (must have fileKey)
   * @param fileName Name for the zip file
   */
  const bulkDownloadZip = async (docs: any[], fileName = "documents.zip") => {
    if (!docs || docs.length === 0) return;
    setDownloading(true);
    try {
      // Robustly extract file keys from doc.media.fileKey or doc.fileKey
      const keys = docs
        .map((doc) => doc.media?.fileKey || doc.fileKey)
        .filter(Boolean);
      if (keys.length === 0) throw new Error("No file keys found");
      const blob = await mediaService.downloadZip(memberId, keys);
      const url = window.URL.createObjectURL(blob);
      const docObj = typeof window !== "undefined" ? window.document : null;
      if (docObj) {
        const a = docObj.createElement("a");
        a.href = url;
        a.download = fileName;
        docObj.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        window.open(url, "_blank");
      }
      // showToast({ position: 'top', message: 'Zip download started', variant: 'success', duration: 4000 });
    } catch (e: any) {
      showToast({
        position: "top",
        message: "Please try again",
        variant: "error",
        duration: 5000,
      });
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Update any field of an uncategorized document.
   * @param doc Document object (must have _id or id)
   * @param update Update payload
   * @param updateLocal Optional callback to update local state (e.g., setDocuments)
   */
  const updateUncategorizedDocument = async (
    doc: any,
    update: Record<string, any>,
    updateLocal?: (updater: (prev: any[]) => any[]) => void
  ) => {
    if (!doc || (!doc._id && !doc.id)) return;
    try {
      await documentsService.updateDocument(doc._id || doc.id, update);
      if (updateLocal) {
        updateLocal((prev: any[]) =>
          prev.map((d) => (d._id === doc._id ? { ...d, ...update } : d))
        );
      }
    } catch (e: any) {
      showToast({
        position: "top",
        message: `Please try again`,
        variant: "error",
        duration: 5000,
      });
      throw e;
    }
  };

  const buildYearFilter = (selectedYears: number[]) => {
    if (!selectedYears || selectedYears.length === 0) return {};
    return { year: { $in: selectedYears } };
  };

  // Vector search method for documents
  const vectorSearchDocuments = async (
    searchTerm: string,
    filters?: any,
    options?: { skipMemories?: boolean }
  ) => {
    const rawResults = await documentsService.vectorSearch(
      searchTerm,
      filters,
      options
    );
    if (!rawResults || !Array.isArray(rawResults.data)) return [];
    return rawResults.data.map((entity: any) => ({
      id: entity._id,
      _id: entity._id,
      displayName: entity.displayName,
      category: entity.category,
      documentDate: entity.documentDate,
      notes: entity.notes,
      isPrivate: entity.isPrivate,
      isImportant: entity.isImportant,
      availableOffline: entity.availableOffline,
      media: entity.media,
      fileKey: entity.media?.fileKey || entity.fileKey,
      fileName: entity.media?.fileName || entity.fileName,
      showComment: entity.notes,
      meta: entity.documentDate,
      ...entity,
    }));
  };

  // Emergency card PDF download using the new endpoint
  const downloadEmergencyCardPdf = async () => {
    return documentsService.downloadEmergencyCardPdf();
  };

  // Wallet card PDF download
  const downloadWalletCardPdf = async () => {
    return documentsService.downloadWalletCardPdf();
  };

  // Fetch actionable insights for a specific document
  const getDocumentInsights = async (documentId: string) => {
    try {
      const response = await actionableInsightsService.getInsightsByDocument(
        documentId
      );
      return response;
    } catch (error: any) {
      console.error("Error fetching document insights:", error);
      showToast({
        position: "top",
        message: "Failed to fetch AI insights",
        variant: "error",
        duration: 5000,
      });
      throw error;
    }
  };

  // Fetch emergency card fileKey (meta)
  const fetchEmergencyCardFileKey = async () => {
    return documentsService.fetchEmergencyCardFileKey();
  };

  const getDocumentCount = useCallback(async () => {
    return documentsService.getDocumentCount();
  }, [documentsService]);

  const getQuickAccessCounts = useCallback(async () => {
    return documentsService.getQuickAccessCounts();
  }, [documentsService]);

  // Fetch uncategorized documents for the member
  const fetchUncategorizedDocuments = async (dto: any) => {
    return await documentsService.getUncategorizedDocuments(dto);
  };

  // Fetch processing (Pending + Processed) documents for the member
  const fetchProcessingDocuments = async (dto: any) => {
    return await documentsService.getProcessingDocuments(dto);
  };

  // Fetch documents by IDs
  const fetchDocumentsByIds = async (ids: string[]) => {
    return await documentsService.getByIds(ids);
  };

  return {
    createDocument,
    uploadDocument,
    getAllDocuments,
    getDocumentTypeCounts,
    getDocumentCount,
    getQuickAccessCounts,
    fetchDocuments,
    fetchDocumentsWithSettings,
    fetchUncategorizedDocuments,
    fetchProcessingDocuments,
    fetchDocumentsByIds,
    downloadDocument,
    bulkDownloadZip,
    bulkUpdateFlag,
    bulkDelete,
    downloading,
    saving,
    saveSuccess,
    saveError,
    setSaveSuccess,
    setSaveError,
    shareDocumentWithDebounce,
    updateDocumentFlag,
    deleteDocument,
    updateDocumentNotes,
    updateUncategorizedDocument,
    buildYearFilter,
    vectorSearchDocuments,
    downloadEmergencyCardPdf,
    downloadWalletCardPdf,
    fetchEmergencyCardFileKey,
    getDocumentInsights,
  };
}
